import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { InterviewManagementFacade } from '@/services/interview';
import { toast } from 'sonner';

export const useDepublishInterview = () => {
  const queryClient = useQueryClient();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [interviewToDepublish, setInterviewToDepublish] = useState<string | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  // Open the confirmation dialog for depublishing
  const confirmDepublish = (interviewId: string) => {
    setInterviewToDepublish(interviewId);
    setIsConfirmDialogOpen(true);
  };

  const handleDepublish = async (interviewId: string) => {
    if (processingId) {
      console.log('Behandler allerede et annet intervju, hopper over');
      return;
    }
    
    try {
      setProcessingId(interviewId);
      toast.loading('Flytter intervju tilbake til ventende...', { id: 'depublish' });
      
      console.log('Attempting to depublish interview:', interviewId);
      const result = await InterviewManagementFacade.depublishInterview(interviewId);
      
      if (result) {
        console.log('Depublish successful, invalidating queries');
        
        // Force an immediate invalidation of ALL relevant queries
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['pendingInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['publishedInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['feederInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['interviews'] })
        ]);
        
        toast.success('Intervju flyttet tilbake til ventende liste', { id: 'depublish' });
        
        // Wait a moment to ensure database consistency
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Force a direct refetch to ensure latest data
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['pendingInterviews'] }),
          queryClient.refetchQueries({ queryKey: ['publishedInterviews'] }),
          queryClient.refetchQueries({ queryKey: ['feederInterviews'] }),
          queryClient.refetchQueries({ queryKey: ['interviews'] })
        ]);
      } else {
        toast.error('Kunne ikke flytte intervju tilbake til ventende', { id: 'depublish' });
        throw new Error('Kunne ikke avpublisere intervju');
      }
    } catch (error) {
      console.error('Feil ved avpublisering:', error);
      toast.error(`Kunne ikke flytte intervju: ${error instanceof Error ? error.message : 'Ukjent feil'}`, { id: 'depublish' });
    } finally {
      setTimeout(() => setProcessingId(null), 500);
    }
  };

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setInterviewToDepublish(null);
  };

  const confirmAndDepublish = () => {
    if (interviewToDepublish) {
      handleDepublish(interviewToDepublish);
      setInterviewToDepublish(null);
    }
    setIsConfirmDialogOpen(false);
  };

  return {
    processingId,
    isConfirmDialogOpen,
    interviewToDepublish,
    confirmDepublish,
    handleDepublish,
    closeConfirmDialog,
    confirmAndDepublish
  };
};
